import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock, Minus, Plus, ShoppingBag, Ticket, Truck, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import SiteNav from "@/components/landing/Nav";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { seoDesc, metaDescription } from "@/lib/seo";

const EASE = [0.16, 1, 0.3, 1];

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [varQty, setVarQty] = useState({});

  useEffect(() => {
    setLoading(true);
    setSize(null);
    setQty(1);
    setVarQty({});
    Promise.all([api.get(`/products/${productId}`), api.get("/products")])
      .then(([p, all]) => {
        setProduct(p.data);
        setRelated(all.data.filter((x) => x.id !== productId).slice(0, 3));
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [productId]);

  const variations = product?.variations || [];
  const hasVariations = variations.length > 0;

  const selectedTotal = useMemo(() => {
    if (!hasVariations) return 0;
    return variations.reduce((s, v) => s + Number(v.price) * (varQty[v.id] || 0), 0);
  }, [hasVariations, variations, varQty]);

  const selectedCount = useMemo(
    () => Object.values(varQty).reduce((s, q) => s + q, 0),
    [varQty]
  );

  const setVar = (id, q) => setVarQty((prev) => ({ ...prev, [id]: Math.max(0, Math.min(10, q)) }));

  const buildItems = () => {
    if (hasVariations) {
      return variations
        .filter((v) => (varQty[v.id] || 0) > 0)
        .map((v) => ({
          type: "product",
          ref_id: product.id,
          variation_id: v.id,
          variation_name: v.name,
          name: `${product.name} — ${v.name}`,
          unit_price: Number(v.price),
          image: product.image,
          quantity: varQty[v.id],
          size: null,
        }));
    }
    return [{
      type: "product", ref_id: product.id, name: product.name,
      unit_price: Number(product.price), image: product.image,
      quantity: qty, size: product.sizes?.length ? size : null,
    }];
  };

  const validate = () => {
    if (hasVariations && selectedCount === 0) {
      toast.error("Choose at least one ticket option.");
      return false;
    }
    if (!hasVariations && product.sizes?.length && !size) {
      toast.error("Please choose a size first.");
      return false;
    }
    return true;
  };

  const addToCart = () => {
    if (!validate()) return;
    buildItems().forEach((item, idx) => cart.add(item, { openDrawer: idx === buildItems().length - 1 }));
    toast.success(selectedCount > 1 ? `${selectedCount} tickets added to cart` : "Added to cart");
  };

  const buyNow = () => {
    if (!validate()) return;
    buildItems().forEach((item) => cart.add(item, { openDrawer: false }));
    navigate("/checkout");
  };

  const ctaLabel = hasVariations
    ? `Buy Now — ${selectedTotal === 0 ? "Free" : `$${selectedTotal.toFixed(2)}`}`
    : `Buy Now — $${(Number(product?.price || 0) * qty).toFixed(2)}`;

  const productJsonLd = product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: metaDescription(seoDesc(product), 200),
        ...(product.image && { image: [new URL(product.image, window.location.origin).href] }),
        brand: { "@type": "Brand", name: "AB Hunter Consulting USA" },
        offers: {
          "@type": "Offer",
          price: (hasVariations ? Math.min(...variations.map((v) => Number(v.price))) : Number(product.price)).toFixed(2),
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          url: `${window.location.origin}/shop/${product.id}`,
        },
      }
    : undefined;

  return (
    <div data-testid="product-page" className="min-h-screen bg-ink text-foreground">
      <Seo
        title={product ? `${product.name} — AB Hunter Consulting USA` : "Shop — AB Hunter Consulting USA"}
        siteName="AB Hunter Consulting USA"
        description={product ? seoDesc(product) : "Shop AB Hunter Consulting USA merchandise and publications."}
        image={product?.image || undefined}
        jsonLd={productJsonLd}
      />
      <SiteNav />
      <div className="lg:pl-64">
        <main className="hero-glow grain relative mx-auto max-w-6xl px-5 pb-24 pt-24 sm:px-8 lg:pt-16">
          <Link to="/" data-testid="product-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Back to shop
          </Link>

          {loading ? (
            <div className="flex justify-center py-32" data-testid="product-loading"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
          ) : !product ? (
            <div className="py-32 text-center" data-testid="product-not-found">
              <p className="font-display text-2xl">Product not found</p>
              <Button asChild className="mt-6 rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover"><Link to="/">Return home</Link></Button>
            </div>
          ) : (
            <>
              <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: EASE }}
                  className="relative"
                >
                  <div className="absolute -inset-3 rounded-[2rem] border border-gold/15" />
                  <div className="relative overflow-hidden rounded-[1.75rem] border border-gold/25 bg-gradient-to-b from-ink-edge to-ink shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]">
                    <img src={product.image || "/assets/product-book.png"} alt={product.name} className="aspect-square w-full object-cover" />
                    <span className="absolute right-5 top-5 rounded-full bg-ink/85 px-4 py-2 font-display text-lg font-semibold text-gold backdrop-blur">
                      {hasVariations
                        ? (() => {
                            const prices = variations.map((v) => Number(v.price));
                            const min = Math.min(...prices); const max = Math.max(...prices);
                            if (min === max) return min === 0 ? "Free" : `$${min.toFixed(2)}`;
                            return min === 0 ? `Free – $${max.toFixed(0)}` : `From $${min.toFixed(0)}`;
                          })()
                        : `$${Number(product.price).toFixed(2)}`}
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">{product.category}</span>
                  <h1 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{product.name}</h1>
                  <p className="mt-5 leading-relaxed text-muted-foreground">{product.description}</p>

                  {hasVariations ? (
                    <div className="mt-8">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        Choose tickets — mix and match for your whole party
                      </p>
                      <div className="mt-4 space-y-3" data-testid="variations-list">
                        {variations.map((v) => {
                          const q = varQty[v.id] || 0;
                          return (
                            <div key={v.id} data-testid={`variation-${v.id}`}
                              className={`flex items-center gap-4 rounded-2xl border p-4 transition-all duration-300 ${q > 0 ? "border-gold/60 bg-gold/5" : "border-gold/15 bg-ink/60"}`}>
                              <Ticket className={`h-5 w-5 shrink-0 ${q > 0 ? "text-gold" : "text-muted-foreground/50"}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold leading-snug">{v.name}</p>
                                {v.description && <p className="mt-0.5 text-xs text-muted-foreground">{v.description}</p>}
                                <p className="mt-1 font-display text-sm font-semibold text-gold">
                                  {Number(v.price) === 0 ? "Free" : `$${Number(v.price).toFixed(2)}`}
                                  <span className="ml-1 font-sans text-xs font-normal text-muted-foreground">each</span>
                                </p>
                              </div>
                              <div className="flex items-center gap-2.5">
                                <button data-testid={`variation-minus-${v.id}`} aria-label="Decrease" onClick={() => setVar(v.id, q - 1)}
                                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold/25 text-gold transition-colors hover:bg-gold/10 disabled:opacity-30"
                                  disabled={q === 0}>
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span data-testid={`variation-qty-${v.id}`} className="w-6 text-center font-display text-base font-semibold">{q}</span>
                                <button data-testid={`variation-plus-${v.id}`} aria-label="Increase" onClick={() => setVar(v.id, q + 1)}
                                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gold/25 text-gold transition-colors hover:bg-gold/10">
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {selectedCount > 0 && (
                        <p className="mt-4 text-right text-sm text-muted-foreground" data-testid="variations-total">
                          {selectedCount} ticket{selectedCount > 1 ? "s" : ""} selected —{" "}
                          <span className="font-display font-semibold text-gold">{selectedTotal === 0 ? "Free" : `$${selectedTotal.toFixed(2)}`}</span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      {product.sizes?.length > 0 && (
                        <div className="mt-8">
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Select size</p>
                          <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Size">
                            {product.sizes.map((s) => (
                              <button
                                key={s}
                                data-testid={`product-size-${s}`}
                                onClick={() => setSize(s)}
                                aria-pressed={size === s}
                                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
                                  size === s ? "border-gold bg-gold text-ink" : "border-gold/25 text-muted-foreground hover:border-gold/60 hover:text-foreground"
                                }`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-8">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Quantity</p>
                        <div className="mt-3 flex items-center gap-3">
                          <button data-testid="product-qty-minus" aria-label="Decrease quantity" onClick={() => setQty(Math.max(1, qty - 1))}
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold transition-colors hover:bg-gold/10">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span data-testid="product-qty" className="w-8 text-center font-display text-lg font-semibold">{qty}</span>
                          <button data-testid="product-qty-plus" aria-label="Increase quantity" onClick={() => setQty(Math.min(20, qty + 1))}
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold transition-colors hover:bg-gold/10">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                    <Button data-testid="product-add-to-cart-button" onClick={addToCart} size="lg" variant="outline"
                      className="flex-1 rounded-full border-gold/50 bg-transparent py-6 font-semibold text-gold transition-all duration-300 hover:bg-gold/10">
                      <ShoppingBag className="mr-2 h-4 w-4" /> Add to Cart
                    </Button>
                    <Button data-testid="product-buy-now-button" onClick={buyNow} size="lg"
                      className="flex-1 rounded-full bg-gold py-6 font-semibold text-ink transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]">
                      {ctaLabel}
                    </Button>
                  </div>

                  <div className="mt-8 space-y-3 border-t border-gold/10 pt-6 text-sm text-muted-foreground">
                    <p className="flex items-center gap-2.5"><Lock className="h-4 w-4 text-gold" /> Secure on-site checkout powered by Stripe</p>
                    {(product?.requires_shipping ?? !hasVariations) ? (
                      <p className="flex items-center gap-2.5"><Truck className="h-4 w-4 text-gold" /> Ships within 5–7 business days</p>
                    ) : (
                      <p className="flex items-center gap-2.5"><Ticket className="h-4 w-4 text-gold" /> Digital tickets — delivered by email, nothing to ship</p>
                    )}
                    <p className="flex items-center gap-2.5"><Zap className="h-4 w-4 text-gold" /> Every purchase funds mentorship programs</p>
                  </div>
                </motion.div>
              </div>

              {related.length > 0 && (
                <div className="mt-24">
                  <h2 className="font-display text-2xl font-semibold">You may also like</h2>
                  <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    {related.map((r) => (
                      <Link key={r.id} to={`/shop/${r.id}`} data-testid={`related-product-${r.id}`}
                        className="group overflow-hidden rounded-2xl border border-gold/15 bg-ink-card/70 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40">
                        <img src={r.image || "/assets/logo.png"} alt={r.name} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="p-4">
                          <p className="truncate text-sm font-semibold">{r.name}</p>
                          <p className="mt-1 font-display text-gold">${Number(r.price).toFixed(2)}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
